import { z } from 'zod';

import { ZodAmount } from '@Common/types/Amount';
import {
  ZodB2bPlanCategoryEr,
  ZodB2bPlanCategoryEsPei,
  ZodB2bPlanCategoryEsPerecoi,
} from '@Common/types/b2b/plans';
import { MergeUnion } from '@Common/types/merge_union';
import {
  ZodInternalSavingPlanId,
  ZodProfileSavingPlanId,
  ZodTransformedProfileSavingPlanId,
} from '@Common/types/profile/saving_plans';
import { ZodId } from '@Common/types/request';
import { ZodSuccessResponse } from '@Common/types/response';
import { ZodDateLike } from '@Common/types/zod/zod-date-like';
import { ZodDateTimeString } from '@Common/types/zod/zod-date-string';
import { endpoint, GetEndpointType } from '@Common/types/zod/zod-endpoint';
import { createZodParamsValidator } from '@Common/types/zod/zod-params';
import { ZodTrimmedString } from '@Common/types/zod/zod-trimmed-string';

export const INVESTOR_PROFILE_HORIZON_AGE_MIN = 0;
export const INVESTOR_PROFILE_HORIZON_AGE_MAX = 70;

export const employeeSavingsRiskProfileValues = [
  'careful',
  'balanced',
  'dynamic',
  'sicav',
] as const;
export const ZodEmployeeSavingsRiskProfile = z.enum(employeeSavingsRiskProfileValues);
export type EmployeeSavingsRiskProfile = z.infer<typeof ZodEmployeeSavingsRiskProfile>;
export const ZodInvestmentHorizonAge = z
  .number()
  .int()
  .gte(INVESTOR_PROFILE_HORIZON_AGE_MIN)
  .lte(INVESTOR_PROFILE_HORIZON_AGE_MAX);

const ZodGetInvestorProfileQuery = createZodParamsValidator(
  z.object({
    saving_plan_id: ZodProfileSavingPlanId,
  }),
);

const ZodInvestorProfilePerecoiPilote = z.strictObject({
  plan_category: ZodB2bPlanCategoryEsPerecoi,
  template_risk_profile_id: ZodId,
  risk_profile: ZodEmployeeSavingsRiskProfile,
  investment_horizon_age: ZodInvestmentHorizonAge,
  is_changeable: z.boolean(),
  min_horizon_age: ZodInvestmentHorizonAge,
  max_horizon_age: ZodInvestmentHorizonAge,
  current_user_age: z.number().int().positive(),
});

export type InvestorProfilePerecoiPilote = z.infer<typeof ZodInvestorProfilePerecoiPilote>;

const ZodInvestorProfilePeiProfile = z.strictObject({
  plan_category: ZodB2bPlanCategoryEsPei,
  template_risk_profile_id: ZodId,
  risk_profile: ZodEmployeeSavingsRiskProfile,
  is_changeable: z.boolean(),
});

export type InvestorProfilePeiProfile = z.infer<typeof ZodInvestorProfilePeiProfile>;

const ZodInvestorProfileErProfile = z.strictObject({
  plan_category: ZodB2bPlanCategoryEr,
  investment_horizon_age: z.number().int(),
  is_changeable: z.boolean(),
  is_confirmed: z.boolean(),
  min_horizon_age: ZodInvestmentHorizonAge,
  max_horizon_age: ZodInvestmentHorizonAge,
  current_user_age: z.number().int().positive(),
});

export type InvestorProfileErProfile = z.TypeOf<typeof ZodInvestorProfileErProfile>;

const investorProfileTypes = [
  ZodInvestorProfilePerecoiPilote,
  ZodInvestorProfilePeiProfile,
  ZodInvestorProfileErProfile,
] as const;
const zodInvestorProfile = z.discriminatedUnion('plan_category', investorProfileTypes);

type InvestorProfile = z.infer<typeof zodInvestorProfile>;

export type InvestorProfileFrontend = MergeUnion<InvestorProfile, 'plan_category'>;

export const getProfileEmployeeSavingsInvestorProfileEndpoint = endpoint({
  method: 'GET',
  queryParams: ZodGetInvestorProfileQuery,
  responseBody: zodInvestorProfile,
});

export type GetProfileEmployeeSavingsInvestorProfileEndpoint = GetEndpointType<
  typeof getProfileEmployeeSavingsInvestorProfileEndpoint
>;

export const EMPLOYEE_SAVINGS_INVESTOR_PROFILE_PENDING_REBALANCING_ERROR =
  'investor_profile/pending_rebalancing';

export const ZodPutProfileEmployeeSavingsInvestorProfileBody = z.object({
  saving_plan_id: ZodTransformedProfileSavingPlanId,
  template_risk_profile_id: ZodId.optional(),
  investment_horizon_age: ZodInvestmentHorizonAge.optional(),
});

export type PutProfileEmployeeSavingsInvestorProfileBody = z.infer<
  typeof ZodPutProfileEmployeeSavingsInvestorProfileBody
>;

const ZodPutProfileEmployeeSavingsInvestorProfileResponse = z.object({
  risk_profile: ZodEmployeeSavingsRiskProfile.nullable(),
  investment_horizon: ZodDateLike.nullable(),
});
export type ZodPutProfileEmployeeSavingsInvestorProfileResponse = z.TypeOf<
  typeof ZodPutProfileEmployeeSavingsInvestorProfileResponse
>;

export const putProfileEmployeeSavingsInvestorProfileEndpoint = endpoint({
  method: 'PUT',
  pathParams: undefined,
  queryParams: undefined,
  body: ZodPutProfileEmployeeSavingsInvestorProfileBody,
  responseBody: ZodPutProfileEmployeeSavingsInvestorProfileResponse.required(),
});

export type PutProfileEmployeeSavingsInvestorProfileEndpoint = GetEndpointType<
  typeof putProfileEmployeeSavingsInvestorProfileEndpoint
>;

const ZodRebalancingEvent = z.object({
  type: z.literal('portfolio_rebalancing'),
  date: ZodDateTimeString,
  saving_plan_id: ZodInternalSavingPlanId,
});

const ZodCaEvent = z.object({
  target_instrument_isin: ZodTrimmedString,
  target_instrument_short_name: ZodTrimmedString,
  initiating_instrument_isin: ZodTrimmedString,
  initiating_instrument_short_name: ZodTrimmedString,
  payable_date: ZodDateTimeString,
  old_rate: z.number(),
  new_rate: z.number(),
  shareholder_letter_b2b_document_id: ZodId.nullable(),
});

const ZodCaMergerEvent = z.object({
  ...ZodCaEvent.shape,
  type: z.literal('ca_merger'),
});

const ZodCaSpinoffEvent = z.object({
  ...ZodCaEvent.shape,
  type: z.literal('ca_spinoff'),
});

export const ZodCaCashDividendEvent = z.object({
  type: z.literal('ca_cash_dividend'),
  instrument_isin: ZodTrimmedString,
  instrument_short_name: ZodTrimmedString,
  dividend_date: ZodDateTimeString,
  cash_per_share: ZodAmount,
  payable_date: ZodDateTimeString,
});

export const ZodUpcomingEvent = z.discriminatedUnion('type', [
  ZodRebalancingEvent,
  ZodCaMergerEvent,
  ZodCaSpinoffEvent,
  ZodCaCashDividendEvent,
]);

export type UpcomingEvent = z.TypeOf<typeof ZodUpcomingEvent>;

export type UpcomingRebalanceEvent = z.TypeOf<typeof ZodRebalancingEvent>;

export type UpcomingCaMergerEvent = z.infer<typeof ZodCaMergerEvent>;

export type UpcomingCaSpinoffEvent = z.infer<typeof ZodCaSpinoffEvent>;

export type UpcomingCaCashDividendEvent = z.infer<typeof ZodCaCashDividendEvent>;

const ZodUpcomingEventsQuery = createZodParamsValidator(
  z.object({
    saving_plan_id: ZodProfileSavingPlanId.optional(),
  }),
);

export const getProfileEmployeeSavingsUpcomingEventsEndpoint = endpoint({
  method: 'GET',
  queryParams: ZodUpcomingEventsQuery,
  responseBody: z.object({
    data: z.array(ZodUpcomingEvent),
  }),
});

export type GetProfileEmployeeSavingsUpcomingEventsEndpoint = GetEndpointType<
  typeof getProfileEmployeeSavingsUpcomingEventsEndpoint
>;

export const putProfileEmployeeSavingsDetailsPersonalEndpoint = endpoint({
  method: 'PUT',
  pathParams: undefined,
  queryParams: undefined,
  body: z.object({
    action: z.enum(['confirm', 'edit']),
  }),
  responseBody: ZodSuccessResponse,
  headers: undefined,
});

export type PutProfileEmployeeSavingsDetailsPersonalEndpoint = GetEndpointType<
  typeof putProfileEmployeeSavingsDetailsPersonalEndpoint
>;

export const ZodInvestorProfileRestrictionReason = z.enum(['suspended', 'sell-only']);
export type InvestorProfileRestrictionReason = z.TypeOf<typeof ZodInvestorProfileRestrictionReason>;

const ZodProfileEmployeeSavingsInvestorProfile = z.object({
  id: ZodId,
  risk_profile: ZodEmployeeSavingsRiskProfile,
  name: ZodTrimmedString,
  is_default: z.boolean(),
  restriction_reason: ZodInvestorProfileRestrictionReason.nullable(),
});

export const getProfileEmployeeSavingsInvestorProfileListEndpoint = endpoint({
  method: 'GET',
  pathParams: undefined,
  queryParams: createZodParamsValidator(
    z.object({
      saving_plan_id: ZodProfileSavingPlanId,
    }),
  ),
  body: undefined,
  responseBody: ZodProfileEmployeeSavingsInvestorProfile.array(),
  headers: undefined,
});

export type GetProfileEmployeeSavingsInvestorProfileListEndpoint = GetEndpointType<
  typeof getProfileEmployeeSavingsInvestorProfileListEndpoint
>;

export type ProfileEmployeeSavingsInvestorProfileList =
  GetProfileEmployeeSavingsInvestorProfileListEndpoint['Client']['ResponseBody'];

export type ProfileEmployeeSavingsInvestorProfile = z.TypeOf<
  typeof ZodProfileEmployeeSavingsInvestorProfile
>;

export const getProfileEmployeeSavingsDocumentsDocumentIdEndpoint = endpoint({
  method: 'GET',
  pathParams: createZodParamsValidator(
    z.object({
      documentId: ZodId,
    }),
  ),
  responseBody: z.object({
    url: ZodTrimmedString.url(),
  }),
  headers: undefined,
});

export type GetProfileEmployeeSavingsDocumentsDocumentIdEndpoint = GetEndpointType<
  typeof getProfileEmployeeSavingsDocumentsDocumentIdEndpoint
>;
